import { useEffect } from "react";

import { useTranslator } from "@eo-locale/react";

import { useRouter, NextRouter } from "next/router";

import { VStack, Button } from "@/storybook-components";
import PasswordQuestion from "../questions/password-question";
import { validateQuestion } from "@/helpers/questions";
import type { SyntheticEvent } from "react";

import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { setPasswordToken, setStatus } from "@/redux/features/users/slice";
import { setError } from "@/redux/features/questions/slice";
import { submitPassword } from "@/redux/features/users/thunk";
import type { Question } from "@/types";

const Form = (): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { answers, errors } = useAppSelector((state) => state.questions);
  const { status } = useAppSelector((state) => state.users);
  const router: NextRouter = useRouter();
  // reset password token is needed to change the password. But incase if it's not available / not valid
  // the api will result in 404. The alert for this is not there at the moment.
  const resetPasswordToken = router.query?.reset_password_token;
  const question: Partial<Question> = {
    id: "forgotPassword",
    question: translate("users.newPassword"),
    type: "SignupForm::PasswordQuestion",
    required: false
  };

  // finding the password inputs has any errors
  const { forgotPassword, confirmPassword } = errors;
  const isForgotPasswordHasError =
    forgotPassword !== undefined && forgotPassword.length === 0;
  const isConfirmPasswordHasError =
    confirmPassword !== undefined && confirmPassword.length === 0;

  const handleSubmit = async (e: SyntheticEvent): Promise<void> => {
    e.preventDefault();
    const error = await validateQuestion(answers[question.id], question);
    if (error?.length) {
      dispatch(setStatus("idle"));
      dispatch(
        setError({
          questionId: question.id,
          error: error
        })
      );
    } else {
      dispatch(
        setPasswordToken({
          resetPasswordToken
        })
      );
      await dispatch(submitPassword());
      dispatch(setStatus({ status: "idle" }));
    }
  };
  useEffect(() => {
    if (status === "reject") {
      router.push({
        pathname: "/users/password/new",
        query: { token: "invalid" }
      });
    } else if (status === "saved") {
      router.push({
        pathname: "/login",
        query: { redirectionStatus: "passwordChanged" }
      });
    }
  }, [status, router]);

  return (
    <VStack my={6} gap={6} as="form" noValidate onSubmit={handleSubmit}>
      <PasswordQuestion key={question.id} question={question} />
      <Button
        variant="primary"
        data-testid="submitButton"
        type="submit"
        width="full"
        disabled={
          status !== "idle" ||
          !(isForgotPasswordHasError && isConfirmPasswordHasError)
        }
      >
        {translate("users.changePassword")}
      </Button>
    </VStack>
  );
};

export default Form;
